import { mutations, queries } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback, useContext } from "react";
import { enqueueSnackbar } from "notistack";
import { useTextDomainContext } from "../../app/providers";

interface UseDeleteNotificationReturn {
  deleteNotification: (_variables: Record<string, any>) => void;
  deleting: boolean;
}

/**
 * Custom hook to delete a notification.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @param {Array} [customRefetchQueries] - Optional custom refetch queries.
 */
export function useDeleteNotification(
  onCompleted: (_data: any) => void = () => {},
  onError: (_err: any) => void = () => {},
  customRefetchQueries?: any[],
): UseDeleteNotificationReturn {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [deleteNotification, { loading }] = useMutation<any>(
    mutations.deleteNotification,
    {
      onCompleted: (_data) => {
        onCompleted(_data);

        enqueueSnackbar(gettext("Notification has been deleted"), {
          variant: "success",
        });
      },
      onError: (_err) => {
        onError(_err);

        enqueueSnackbar("Delete Notification: " + _err.message, {
          variant: "error",
        });
      },
      update: (cache, _result, { variables }) => {
        if (variables?.notificationId) {
          cache.evict({
            id: cache.identify({
              __typename: "NotificationType",
              id: variables.notificationId,
            }),
          });
          cache.gc();
        }
      },
      refetchQueries: customRefetchQueries ?? [queries.getNewNotifications],
    },
  );

  const deleteNotificationRow = useCallback(
    (_variables: Record<string, any>) => {
      deleteNotification({
        variables: _variables,
      }).then();
    },
    [deleteNotification],
  );

  return {
    deleteNotification: deleteNotificationRow,
    deleting: loading,
  };
}
